import React, { useEffect, useState, useRef } from 'react'
import { BrowserRouter as Router } from 'react-router-dom'
import { authService } from './services/authService'
import { AuthProvider } from './contexts/AuthContext'
import { WalletProvider } from './contexts/WalletContext'
import { UserInfoProvider } from './contexts/UserInfoContext'
import { BankProvider } from './contexts/BankContext'
import AppRoutes from './components/AppRoutes'
import TenantStatusGuard from './components/TenantStatusGuard'
import DevToolsPanel from './components/DevToolsPanel'
import { TenantThemeContext } from './hooks/useTenantThemeConfig'
import type { TenantThemeConfig, ColorScheme } from './types/theme'
import { COLOR_SCHEMES } from './types/theme'
import { getThemeById, getDefaultTheme, loadThemesFromAPI } from './config/themes'
import { getDefaultThemeForTenant, getTenantConfig, loadTenantsFromAPI } from './config/tenants'
import { getTenantContent } from './services/tenantService'
import { lotteryService } from './services/lotteryService'
import { useSmartWebSocket } from './hooks/useSmartWebSocket'
import { usePopupManagerOptimized } from './hooks/usePopupManagerOptimized'
import PopupModal from './components/PopupModal'
import { ClassicAnnouncementAlert } from './components/ClassicAnnouncementAlert'
import { Toaster } from 'react-hot-toast'
import useRateLimitNotification from './hooks/useRateLimitNotification'
import SystemMaintenancePage from './pages/SystemMaintenancePage'
import { getCdnBaseUrl } from './utils/cdnUtils'
import { getOptionalTenantId, resolveTenantId } from './utils/tenantUtils'
import { getThemeAssetUrl, getThemeMetadata } from './utils/themeUtils'
import { withApiBaseUrl } from './lib/apiBaseUrl'
import { parseLiveTvChannels, parseHuayYoutubeLive } from './lib/liveTv'
import { updateSocialMetaTags } from './utils/socialMeta'
import {
  fetchMaintenanceStatusCached,
  type MaintenanceState,
} from '@huay/shared/tenantManagementPublicClient'

// ─── Maintenance mode ─────────────────────────────────────────────────────────
interface MaintenanceWarning {
  active: boolean
  reason?: string
  estimatedEnd?: string | null
  maintenanceStart?: string | null
  countdownMinutes: number
  secondsLeft: number
}
// WebSocket handles real-time updates; polling is just a fallback
const MAINTENANCE_CHECK_INTERVAL = 120_000

function getApiBaseUrl(): string {
  const sample = withApiBaseUrl('/api/v1/tenant-management/maintenance')
  return sample.replace(/\/api\/v1\/tenant-management\/maintenance$/, '')
}

async function fetchMaintenanceStatus(tenantId?: string): Promise<MaintenanceState> {
  return fetchMaintenanceStatusCached(getApiBaseUrl(), tenantId)
}

// ─── Maintenance Warning Overlay ──────────────────────────────────────────────
const MaintenanceWarningOverlay: React.FC<{ warning: MaintenanceWarning }> = ({ warning }) => {
  const mins = Math.floor(warning.secondsLeft / 60)
  const secs = warning.secondsLeft % 60
  const pct = Math.max(0, (warning.secondsLeft / (warning.countdownMinutes * 60)) * 100)
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 999990,
      background: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backdropFilter: 'blur(6px)'
    }}>
      <div style={{
        background: 'linear-gradient(135deg,#1a1a2e,#16213e)',
        border: '2px solid #f59e0b',
        borderRadius: 20, padding: '36px 40px', maxWidth: 460, width: '90%',
        boxShadow: '0 0 60px rgba(245,158,11,0.3)',
        textAlign: 'center', color: '#fff', fontFamily: 'sans-serif'
      }}>
        <div style={{ fontSize: 56, marginBottom: 12 }}>⚠️</div>
        <h2 style={{ color: '#f59e0b', fontSize: 22, fontWeight: 700, margin: '0 0 8px' }}>
          ระบบกำลังจะปิดให้บริการชั่วคราว
        </h2>
        {warning.reason && (
          <p style={{ color: '#d1d5db', fontSize: 14, margin: '0 0 16px' }}>
            {warning.reason}
          </p>
        )}
        <div style={{
          background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.4)',
          borderRadius: 12, padding: '16px 24px', marginBottom: 20
        }}>
          <p style={{ color: '#9ca3af', fontSize: 12, margin: '0 0 4px' }}>ระบบจะ logout อัตโนมัติใน</p>
          <div style={{ fontSize: 48, fontWeight: 800, color: warning.secondsLeft <= 30 ? '#ef4444' : '#f59e0b', lineHeight: 1 }}>
            {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
          </div>
          <p style={{ color: '#9ca3af', fontSize: 12, margin: '6px 0 0' }}>นาที : วินาที</p>
        </div>
        {/* Progress bar */}
        <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 99, height: 6, overflow: 'hidden', marginBottom: 16 }}>
          <div style={{
            height: '100%', background: warning.secondsLeft <= 30 ? '#ef4444' : '#f59e0b',
            width: `${pct}%`, transition: 'width 1s linear, background 0.5s'
          }} />
        </div>
        {warning.maintenanceStart && (
          <p style={{ color: '#9ca3af', fontSize: 12, margin: '0 0 4px' }}>
            เริ่มปิดระบบ: <strong style={{ color: '#d1d5db' }}>
              {new Date(warning.maintenanceStart).toLocaleString('th-TH', { timeZone: 'Asia/Bangkok', dateStyle: 'medium', timeStyle: 'short' })}
            </strong>
          </p>
        )}
        {warning.estimatedEnd && (
          <p style={{ color: '#9ca3af', fontSize: 12, margin: '4px 0 0' }}>
            คาดว่าเปิดอีกครั้ง: <strong style={{ color: '#d1d5db' }}>
              {new Date(warning.estimatedEnd).toLocaleString('th-TH', { timeZone: 'Asia/Bangkok', dateStyle: 'medium', timeStyle: 'short' })}
            </strong>
          </p>
        )}
        <p style={{ color: '#6b7280', fontSize: 11, marginTop: 16 }}>
          กรุณาบันทึกข้อมูลก่อนเวลาหมด
        </p>
      </div>
    </div>
  )
}

// Asset configuration: default ใช้ S3 (cdn-assets), override ได้จาก window.CDN_BASE_URL (injected at build)
const DEFAULT_CDN_URL = 'https://huay-cdn-assets-prod.s3.ap-southeast-1.amazonaws.com'
const getAssetConfig = () => ({
  CDN_BASE_URL: getCdnBaseUrl() || DEFAULT_CDN_URL,
  CSS_PATH: '/css',
  IMG_PATH: '/img',
  FONTS_PATH: '/fonts'
})

const App: React.FC = () => {
  const [themeConfig, setThemeConfig] = useState<TenantThemeConfig | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastContentHash, setLastContentHash] = useState<string>('')
  const [tenantId, setTenantId] = useState<string>('')
  const themeLoadedRef = useRef(false) // Prevent duplicate theme loading
  const [maintenance, setMaintenance] = useState<MaintenanceState>({ enabled: false })
  const [maintWarn, setMaintWarn] = useState<MaintenanceWarning>({ active: false, countdownMinutes: 0, secondsLeft: 0 })
  const maintWarnTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // View As: เมื่อ admin เปิดแอปด้วย #/dashboard?viewAsToken=xxx (port 5174)
  const [viewAsHandling, setViewAsHandling] = useState(() => authService.getViewAsTokenFromHash() !== null)
  const [viewAsError, setViewAsError] = useState<string | null>(null)

  // Rate limiting notifications
  useRateLimitNotification();

  // ── Maintenance mode check (poll every 15 s + WebSocket instant update) ─────
  useEffect(() => {
    let active = true
    let prevEnabled = false
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : ''
    const isAuthRoute = currentPath === '/login' || currentPath === '/register' || currentPath === '/register-complete'
    const check = () => fetchMaintenanceStatus(tenantId || undefined).then(s => {
      if (!active) return
      if (s.enabled && !prevEnabled) {
        // Transition: off → on (poll-detected) — force logout
        authService.logout().catch(() => {})
      }
      prevEnabled = s.enabled
      setMaintenance(s)
    })
    check()
    if (isAuthRoute) {
      return () => { active = false }
    }
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
      check()
    }, MAINTENANCE_CHECK_INTERVAL)
    return () => { active = false; clearInterval(id) }
  }, [tenantId])

  // ── Maintenance events from shared content WebSocket ──────────────────────────
  useEffect(() => {
    if (!tenantId) return
    const handleMaintenanceModeChanged = (event: Event) => {
      const data = (event as CustomEvent<MaintenanceState & { maintenanceStart?: string | null; tenantId?: string }>).detail
      if (data?.tenantId && data.tenantId !== tenantId) return
      setMaintenance({ enabled: data.enabled === true, reason: data.reason, estimatedEnd: data.estimatedEnd })
      setMaintWarn({ active: false, countdownMinutes: 0, secondsLeft: 0 })
      if (maintWarnTimerRef.current) { clearInterval(maintWarnTimerRef.current); maintWarnTimerRef.current = null }
      if (data.enabled) {
        authService.logout().catch(() => {})
      }
    }

    const handleMaintenanceWarning = (event: Event) => {
      const data = (event as CustomEvent<MaintenanceWarning & { tenantId?: string }>).detail
      if (data?.tenantId && data.tenantId !== tenantId) return
      const totalSeconds = (data.countdownMinutes || 5) * 60
      setMaintWarn({
        active: true,
        reason: data.reason,
        estimatedEnd: data.estimatedEnd,
        maintenanceStart: data.maintenanceStart,
        countdownMinutes: data.countdownMinutes || 5,
        secondsLeft: totalSeconds,
      })
      if (maintWarnTimerRef.current) clearInterval(maintWarnTimerRef.current)
      maintWarnTimerRef.current = setInterval(() => {
        setMaintWarn(prev => {
          if (!prev.active) return prev
          const next = prev.secondsLeft - 1
          if (next <= 0) {
            if (maintWarnTimerRef.current) { clearInterval(maintWarnTimerRef.current); maintWarnTimerRef.current = null }
            return { ...prev, secondsLeft: 0 }
          }
          return { ...prev, secondsLeft: next }
        })
      }, 1000)
    }

    window.addEventListener('maintenance_mode_changed', handleMaintenanceModeChanged as EventListener)
    window.addEventListener('maintenance_warning', handleMaintenanceWarning as EventListener)
    return () => {
      window.removeEventListener('maintenance_mode_changed', handleMaintenanceModeChanged as EventListener)
      window.removeEventListener('maintenance_warning', handleMaintenanceWarning as EventListener)
      if (maintWarnTimerRef.current) { clearInterval(maintWarnTimerRef.current); maintWarnTimerRef.current = null }
    }
  }, [tenantId])

  // View As: apply token from hash then redirect to /dashboard (ไม่ต้องใส่ username/password)
  useEffect(() => {
    const token = authService.getViewAsTokenFromHash()
    if (!token) {
      setViewAsHandling(false)
      return
    }
    setViewAsError(null)
    authService.applyViewAsToken(token).then((result) => {
      if (result.ok) {
        window.location.replace(window.location.origin + '/dashboard')
        return
      }
      setViewAsError(result.error || 'ไม่สามารถเข้า View As ได้')
      setViewAsHandling(false)
    })
  }, [])


  // Get tenant info and load appropriate theme
  useEffect(() => {
    loadTenantTheme()
  }, [])



  // Real-time content updates via WebSocket (only update state when content actually changed to avoid unnecessary re-renders/refresh)
  const handleContentUpdate = async (newContent: any) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Content updated via WebSocket, refreshing UI...', newContent)
    }
    
    // If this is a refresh trigger, reload tenant content from API
    if (newContent._refreshTrigger) {
      console.log('🔄 Refresh trigger received, reloading tenant content...')
      try {
        await loadTenantTheme()
        return
      } catch (error) {
        console.error('❌ Failed to reload tenant content:', error)
        return
      }
    }
    
    const nextWebChat = (newContent?.features && typeof newContent.features?.webChatEnabled === 'boolean')
      ? newContent.features.webChatEnabled
      : undefined
    const nextDepositEnabled = (newContent?.features && typeof newContent.features?.depositEnabled === 'boolean')
      ? newContent.features.depositEnabled
      : undefined
    const nextWithdrawEnabled = (newContent?.features && typeof newContent.features?.withdrawEnabled === 'boolean')
      ? newContent.features.withdrawEnabled
      : undefined
    const nextLotteryEnabled = (newContent?.features && typeof newContent.features?.lotteryEnabled === 'boolean')
      ? newContent.features.lotteryEnabled
      : undefined
    const nextCasinoEnabled = (newContent?.features && typeof newContent.features?.casinoEnabled === 'boolean')
      ? newContent.features.casinoEnabled
      : undefined
    const nextWipWup = (typeof newContent?.features?.wipWupMinutes === 'number' && newContent.features.wipWupMinutes >= 1 && newContent.features.wipWupMinutes <= 120)
      ? newContent.features.wipWupMinutes
      : undefined
    const nextWipWupOther = (typeof newContent?.features?.wipWupMinutesOther === 'number' && newContent.features.wipWupMinutesOther >= 1 && newContent.features.wipWupMinutesOther <= 120)
      ? newContent.features.wipWupMinutesOther
      : undefined
    const nextSessionIdle = (typeof newContent?.features?.sessionIdleMinutes === 'number' && newContent.features.sessionIdleMinutes >= 0 && newContent.features.sessionIdleMinutes <= 120)
      ? newContent.features.sessionIdleMinutes
      : undefined
    const nextLiveTvEnabled = (typeof newContent?.features?.liveTvEnabled === 'boolean')
      ? newContent.features.liveTvEnabled
      : undefined
    const nextPredictionEnabled = (typeof newContent?.features?.predictionEnabled === 'boolean')
      ? newContent.features.predictionEnabled
      : undefined
    const nextLiveTvChannels = newContent?.features?.liveTvChannels !== undefined
      ? parseLiveTvChannels(newContent.features.liveTvChannels)
      : undefined
    const nextHuayYoutubeLive = (newContent?.features as { huayYoutubeLive?: unknown } | undefined)?.huayYoutubeLive !== undefined
      ? parseHuayYoutubeLive((newContent.features as { huayYoutubeLive?: unknown }).huayYoutubeLive)
      : undefined
    const nextLiveTvPlayerStartMuted = typeof (newContent?.features as { liveTvPlayerStartMuted?: boolean } | undefined)?.liveTvPlayerStartMuted === 'boolean'
      ? (newContent.features as { liveTvPlayerStartMuted?: boolean }).liveTvPlayerStartMuted
      : undefined
    const nextMarquee = newContent.content?.marqueeText ?? newContent.marqueeText
    const nextWelcome = newContent.content?.welcomeMessage ?? newContent.welcomeMessage
    const nextSiteTitle = (newContent.content?.siteTitle ?? newContent.siteTitle)?.trim?.()
    const nextLogoUrl = sanitizeBrandingAssetUrl(newContent.branding?.logo)
    const nextFaviconUrl = sanitizeBrandingAssetUrl(newContent.branding?.favicon)

    // Update document.title immediately when siteTitle changes (real-time, no reload)
    if (nextSiteTitle !== undefined && nextSiteTitle !== '') {
      document.title = nextSiteTitle
    }

    setThemeConfig(prev => {
      if (!prev) return null
      const nextSettings = {
        ...prev.settings,
        ...(nextMarquee !== undefined && { marqueeText: nextMarquee }),
        ...(nextWelcome !== undefined && { welcomeMessage: nextWelcome }),
        contactInfo: newContent.content?.contactInfo ?? newContent.contactInfo ?? prev.settings?.contactInfo,
        socialLinks: newContent.content?.socialLinks ?? newContent.socialLinks ?? prev.settings?.socialLinks,
        rulesAndRegulations: newContent.content?.rulesAndRegulations ?? newContent.rulesAndRegulations ?? prev.settings?.rulesAndRegulations,
        paymentRates: newContent.content?.paymentRates ?? newContent.paymentRates ?? prev.settings?.paymentRates,
        webChatEnabled: nextWebChat !== undefined ? nextWebChat : (prev?.settings?.webChatEnabled !== false),
        depositEnabled: nextDepositEnabled !== undefined ? nextDepositEnabled : (prev?.settings?.depositEnabled !== false),
        withdrawEnabled: nextWithdrawEnabled !== undefined ? nextWithdrawEnabled : (prev?.settings?.withdrawEnabled !== false),
        lotteryEnabled: nextLotteryEnabled !== undefined ? nextLotteryEnabled : (prev?.settings?.lotteryEnabled !== false),
        casinoEnabled: nextCasinoEnabled !== undefined ? nextCasinoEnabled : (prev?.settings?.casinoEnabled !== false),
        ...(nextWipWup !== undefined && { wipWupMinutes: nextWipWup }),
        ...(nextWipWupOther !== undefined && { wipWupMinutesOther: nextWipWupOther }),
        ...(nextSessionIdle !== undefined && { sessionIdleMinutes: nextSessionIdle }),
        ...(nextLiveTvEnabled !== undefined && { liveTvEnabled: nextLiveTvEnabled }),
        ...(nextPredictionEnabled !== undefined && { predictionEnabled: nextPredictionEnabled }),
        ...(nextLiveTvChannels !== undefined && { liveTvChannels: nextLiveTvChannels }),
        ...(nextHuayYoutubeLive !== undefined && { huayYoutubeLive: nextHuayYoutubeLive }),
        ...(nextLiveTvPlayerStartMuted !== undefined && { liveTvPlayerStartMuted: nextLiveTvPlayerStartMuted }),
      }
      let nextBranding = nextSiteTitle !== undefined
        ? { ...prev.theme.branding, siteName: nextSiteTitle }
        : prev.theme.branding
      if (nextLogoUrl !== undefined && nextLogoUrl !== '') nextBranding = { ...nextBranding, logoUrl: nextLogoUrl }
      if (nextFaviconUrl !== undefined && nextFaviconUrl !== '') nextBranding = { ...nextBranding, faviconUrl: nextFaviconUrl }
      // Skip update if nothing meaningful changed (avoids Lotto page re-render + animation replay)
      const prevWebChat = prev.settings?.webChatEnabled
      const prevDepositEnabled = prev.settings?.depositEnabled
      const prevWithdrawEnabled = prev.settings?.withdrawEnabled
      const prevMarquee = prev.settings?.marqueeText
      const prevWelcome = prev.settings?.welcomeMessage
      const prevSiteName = prev.theme.branding?.siteName
      const prevLogoUrl = prev.theme.branding?.logoUrl
      const prevFaviconUrl = prev.theme.branding?.faviconUrl
      const prevWipWup = prev.settings?.wipWupMinutes
      const prevWipWupOther = prev.settings?.wipWupMinutesOther
      const prevSessionIdle = prev.settings?.sessionIdleMinutes
      if (
        nextSettings.webChatEnabled === prevWebChat &&
        nextSettings.depositEnabled === prevDepositEnabled &&
        nextSettings.withdrawEnabled === prevWithdrawEnabled &&
        nextSettings.marqueeText === prevMarquee &&
        nextSettings.welcomeMessage === prevWelcome &&
        nextBranding.siteName === prevSiteName &&
        (nextLogoUrl === undefined || nextLogoUrl === prevLogoUrl) &&
        (nextFaviconUrl === undefined || nextFaviconUrl === prevFaviconUrl) &&
        nextSettings.contactInfo === prev.settings?.contactInfo &&
        nextSettings.socialLinks === prev.settings?.socialLinks &&
        nextSettings.rulesAndRegulations === prev.settings?.rulesAndRegulations &&
        nextSettings.paymentRates === prev.settings?.paymentRates &&
        (nextWipWup === undefined || nextWipWup === prevWipWup) &&
        (nextWipWupOther === undefined || nextWipWupOther === prevWipWupOther) &&
        (nextSessionIdle === undefined || nextSessionIdle === prevSessionIdle) &&
        (nextLiveTvEnabled === undefined || nextLiveTvEnabled === prev.settings?.liveTvEnabled) &&
        (nextLiveTvChannels === undefined || JSON.stringify(nextLiveTvChannels) === JSON.stringify(prev.settings?.liveTvChannels)) &&
        (nextHuayYoutubeLive === undefined || JSON.stringify(nextHuayYoutubeLive) === JSON.stringify(prev.settings?.huayYoutubeLive)) &&
        (nextLiveTvPlayerStartMuted === undefined || nextLiveTvPlayerStartMuted === prev.settings?.liveTvPlayerStartMuted)
      ) {
        return prev
      }
      return {
        ...prev,
        theme: { ...prev.theme, branding: nextBranding },
        settings: nextSettings
      }
    })
    // อัปเดต favicon ใน document ทันทีเมื่อ WebSocket ส่ง branding.favicon มา
    if (nextFaviconUrl && nextFaviconUrl.trim() !== '') {
      updateFavicon(nextFaviconUrl)
    }
    if (nextSiteTitle || nextLogoUrl) {
      updateSocialMetaTags({
        title: nextSiteTitle || document.title,
        description: nextWelcome || nextMarquee || document.title,
        imageUrl: nextLogoUrl,
        url: window.location.origin,
      })
    }
    setLastContentHash(JSON.stringify(newContent))
  }

  // Use Smart WebSocket for real-time updates (auto-upgrade between anonymous/authenticated)
  const webSocketEnabled = !!tenantId && !!themeConfig;
  const { isConnected, authType, disconnect: disconnectWebSocket } = useSmartWebSocket({
    tenantId,
    onContentUpdate: handleContentUpdate,
    enabled: webSocketEnabled
  })

  // 🎪 Optimized Popup management system (WebSocket-first, homepage-only)
  // Enable popup manager as soon as we have tenantId (don't wait for themeConfig)
  const popupManagerEnabled = !!tenantId;
  const {
    currentPopup,
    popupQueue,
    isLoading: popupLoading,
    error: popupError,
    dismissPopup,
    forceShowPopup,
    refreshPopups
  } = usePopupManagerOptimized({
    tenantId,
    route: '/', // Homepage route for popup display
    enabled: popupManagerEnabled
  })

  // Debug WebSocket enablement (reduced logging)
  // WebSocket waiting logging ถูกปิดเพื่อลด spam logs

  // Debug Popup enablement (reduced logging)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && popupError) {
      console.error('🎪 Popup Error:', popupError);
    }
  }, [tenantId, themeConfig, popupManagerEnabled, currentPopup, popupQueue.length, popupLoading, popupError])

  // Log connection status changes (reduced logging)
  useEffect(() => {
    // WebSocket connection status logging ถูกปิดเพื่อลด spam logs
  }, [isConnected, authType, tenantId])

  // Fallback polling when WebSocket is disconnected (e.g. after idle) so content updates
  // (marquee, webChat toggle, etc.) still apply within ~45s until socket reconnects
  const FALLBACK_POLL_INTERVAL_MS = 45000 // 45 seconds when WS disconnected
  useEffect(() => {
    if (!themeConfig || isConnected) return

    if (process.env.NODE_ENV === 'development') {
      console.log('🔌 Smart WebSocket not connected, using fallback polling...')
    }

    const intervalId = setInterval(async () => {
      try {
        const currentTenantId = await resolveTenantId()
        const response = await getTenantContent(currentTenantId)

        if (response.success) {
          const contentHash = JSON.stringify({
            content: response.data.content,
            features: response.data.features,
          })

          if (contentHash !== lastContentHash && lastContentHash !== '') {
            if (process.env.NODE_ENV === 'development') {
              console.log('🔄 Content updated via fallback polling...')
            }
            // Pass full tenant data (content + features) so marquee and webChatEnabled etc. update
            handleContentUpdate(response.data)
          }

          setLastContentHash(contentHash)
        }
      } catch (error) {
        console.warn('Fallback polling failed:', error)
      }
    }, FALLBACK_POLL_INTERVAL_MS)

    return () => clearInterval(intervalId)
  }, [themeConfig, lastContentHash, isConnected])

  const loadTenantTheme = async () => {
    // Prevent duplicate calls during React StrictMode
    if (themeLoadedRef.current) {
      console.log('⏭️ Theme already loaded, skipping duplicate call')
      return
    }
    
    themeLoadedRef.current = true
    
    try {
      console.log('🎨 Loading tenant theme and content...')
      setIsLoading(true)
      setError(null)
      
      // Load configurations from API (in production this would be from API)
      await loadThemesFromAPI()
      await loadTenantsFromAPI()
      
      // Resolve tenant ID from runtime sources first, then domain-mappings on production hosts.
      const currentTenantId = await resolveTenantId()
      const resolvedTenantId = currentTenantId
      setTenantId(resolvedTenantId)
      lotteryService.setTenantId(resolvedTenantId)
      // Tenant ID logging ถูกปิดเพื่อลด spam logs
      const tenantContentResponse = await getTenantContent(resolvedTenantId)

      if (!tenantContentResponse?.success && tenantContentResponse?.data == null) {
        throw new Error('Failed to load tenant content from API')
      }

      // Support both { success, data } and direct content object
      const tenantContentData = tenantContentResponse?.data ?? tenantContentResponse
      const tenantContent = (tenantContentData?.content && typeof tenantContentData.content === 'object')
        ? tenantContentData.content
        : {}
      const tenantBranding = (tenantContentData?.branding && typeof tenantContentData.branding === 'object')
        ? tenantContentData.branding
        : {}
      const tenantFeatures = (tenantContentData?.features && typeof tenantContentData.features === 'object')
        ? tenantContentData.features
        : {}
      
      // Get tenant configuration (fallback for theme detection)
      const tenantConfig = getTenantConfig(currentTenantId || '')
      // Tenant config logging ถูกปิดเพื่อลด spam logs
      
      // Determine preferred theme
      // Priority: admin-configured template > saved user preference > static default
      const savedTheme = localStorage.getItem('huay-classic-theme')

      // Extract sub-theme from frontendTemplate (e.g. "user-classic/huayfast" → "huay-fast")
      const rawTemplate: string = (tenantContentData as any)?.data?.frontendTemplate
          || (tenantContentData as any)?.frontendTemplate
          || ''
      const subThemePart = rawTemplate.includes('/') ? rawTemplate.split('/')[1] : ''
      // Normalise: "huayfast" → "huay-fast", "huayasia" → "huay-asia", "huaypremium" → "huay-premium"
      const adminTheme = subThemePart
          ? subThemePart.replace(/^huay/, 'huay-').replace('--', '-')
          : ''

      const tenantDefaultTheme = tenantConfig?.defaultTheme || getDefaultThemeForTenant(currentTenantId || '')
      const preferredThemeId = adminTheme || savedTheme || tenantDefaultTheme

      // Get theme metadata from dynamic configuration
      const themeMetadata = getThemeById(preferredThemeId) || getDefaultTheme()
      // Theme selection logging ถูกปิดเพื่อลด spam logs

      // 🔧 Build configuration from API data + theme metadata
      const apiConfig = {
          theme: {
          id: themeMetadata.id,
          name: themeMetadata.name,
            layout: 'classic',
          colors: themeMetadata.colorScheme
          },
          branding: {
          siteName: (tenantContent as any)?.siteTitle?.trim() || `${tenantContentData.name} :: แทงหวยออนไลน์ ${tenantContentData.name} การเงินมั่นคงปลอดภัยที่สุด`,
          logoUrl: sanitizeBrandingAssetUrl((tenantBranding as any)?.logo),
          faviconUrl: sanitizeBrandingAssetUrl((tenantBranding as any)?.favicon),
          footerText: `Copyright © 2023-2024 ${tenantContentData.name}. All rights reserved.`
        },
        customization: {
          customCSS: undefined // Theme-specific custom CSS if needed
        },
        settings: {
          marqueeText: (tenantContent as any)?.marqueeText,
          welcomeMessage: (tenantContent as any)?.welcomeMessage,
          contactInfo: (tenantContent as any)?.contactInfo,
          socialLinks: (tenantContent as any)?.socialLinks
        },
        tenant: {
          ...tenantConfig,
          name: tenantContentData.name,
          description: tenantContentData.description,
          branding: tenantBranding
        }
      }
      
      if (apiConfig.theme.layout === 'classic') {
        const finalTenantId = currentTenantId
        
        const tenantThemeConfig: TenantThemeConfig = {
          tenantId: finalTenantId,
          themeId: apiConfig.theme.id, // Add theme ID for CSS loading
          theme: {
            baseTheme: 'classic',
            colorScheme: mapApiColorsToColorScheme(apiConfig.theme.colors, null),
            branding: {
              siteName: apiConfig.branding?.siteName || `${tenantContentData.name} :: แทงหวยออนไลน์`,
              logoUrl: sanitizeBrandingAssetUrl(apiConfig.branding?.logoUrl || (tenantBranding as any)?.logo) || getDefaultLogo(themeMetadata.id),
              faviconUrl: sanitizeBrandingAssetUrl(apiConfig.branding?.faviconUrl || (tenantBranding as any)?.favicon),
              footerText: apiConfig.branding?.footerText || `Copyright © 2023-2024 ${tenantContentData.name}. All rights reserved.`
            },
            customCSS: apiConfig.customization?.customCSS
          },
          settings: {
            marqueeText: apiConfig.settings?.marqueeText || (tenantContent as any)?.marqueeText,
            welcomeMessage: apiConfig.settings?.welcomeMessage || (tenantContent as any)?.welcomeMessage,
            contactInfo: apiConfig.settings?.contactInfo || (tenantContent as any)?.contactInfo,
            socialLinks: apiConfig.settings?.socialLinks || (tenantContent as any)?.socialLinks,
            rulesAndRegulations: (apiConfig.settings as any)?.rulesAndRegulations || (tenantContent as any)?.rulesAndRegulations,
            paymentRates: (apiConfig.settings as any)?.paymentRates || (tenantContent as any)?.paymentRates,
            webChatEnabled: (tenantFeatures as any)?.webChatEnabled === false ? false : true,
            depositEnabled: (tenantFeatures as any)?.depositEnabled === false ? false : true,
            withdrawEnabled: (tenantFeatures as any)?.withdrawEnabled === false ? false : true,
            lotteryEnabled: (tenantFeatures as any)?.lotteryEnabled === false ? false : true,
            casinoEnabled: (tenantFeatures as any)?.casinoEnabled === false ? false : true,
            wipWupMinutes: (() => {
              const v = (tenantFeatures as any)?.wipWupMinutes
              return typeof v === 'number' && v >= 1 && v <= 120 ? v : 15
            })(),
            wipWupMinutesOther: (() => {
              const v = (tenantFeatures as any)?.wipWupMinutesOther
              return typeof v === 'number' && v >= 1 && v <= 120 ? v : 15
            })(),
            sessionIdleMinutes: (() => {
              const v = (tenantFeatures as any)?.sessionIdleMinutes
              return typeof v === 'number' && v >= 0 && v <= 120 ? v : 30
            })(),
            liveTvChannels: parseLiveTvChannels((tenantFeatures as any)?.liveTvChannels),
            liveTvEnabled: (tenantFeatures as any)?.liveTvEnabled === true,
            predictionEnabled: (tenantFeatures as any)?.predictionEnabled === true,
            huayYoutubeLive: parseHuayYoutubeLive((tenantFeatures as any)?.huayYoutubeLive),
            liveTvPlayerStartMuted: (tenantFeatures as any)?.liveTvPlayerStartMuted !== false,
          }
        }
        
        // Theme config set logging ถูกปิดเพื่อลด spam logs
        setThemeConfig(tenantThemeConfig)
        
        // Set initial content hash for change detection
        setLastContentHash(JSON.stringify(tenantContent))

        // Unblock the app as soon as runtime config is ready.
        // Legacy theme assets can continue loading in the background.
        setIsLoading(false)

        void loadThemeAssets(tenantThemeConfig)
          .then(() => {
            if (process.env.NODE_ENV === 'development') {
              console.log('✅ Theme assets loaded successfully')
            }
          })
          .catch((assetError) => {
            console.warn('⚠️ Theme assets finished with warnings:', assetError)
          })
      } else {
        throw new Error(`This frontend only supports classic layout, received: ${apiConfig.theme.layout}`)
      }
    } catch (error) {
      console.error('❌ Failed to load tenant theme:', error)
      setError(error instanceof Error ? error.message : 'Failed to load theme')
      themeLoadedRef.current = false // Reset on error to allow retry

      // Never fall back to another production tenant. Keep the last known tenant if available.
      const catchFallbackTenantId =
        tenantId ||
        getOptionalTenantId() ||
        (import.meta.env.PROD ? '' : 'test-demo-01')
      setTenantId(catchFallbackTenantId)
      if (catchFallbackTenantId) {
        lotteryService.setTenantId(catchFallbackTenantId)
      }

      // Fallback to default
      const defaultConfig = createDefaultThemeConfigSafe(catchFallbackTenantId)
      setThemeConfig(defaultConfig)
      setIsLoading(false)
      void loadThemeAssets(defaultConfig).catch((assetError) => {
        console.warn('⚠️ Default theme assets finished with warnings:', assetError)
      })
    } finally {
      setIsLoading(false)
    }
  }

  const loadThemeAssets = async (config: TenantThemeConfig) => {
    const ASSET_CONFIG = getAssetConfig()
    
    // Get theme metadata from dynamic configuration
    const themeMetadata = getThemeById(config.themeId)
    if (!themeMetadata) {
      throw new Error(`Theme ${config.themeId} not found in configuration`)
    }
    
    // Theme loading name logging ถูกปิดเพื่อลด spam logs
    
    const themeAssets = {
      folder: themeMetadata.folder,
      styles: themeMetadata.assets.styles,
      scripts: themeMetadata.assets.scripts,
      favicon: themeMetadata.assets.favicon,
      logo: themeMetadata.assets.logo
    }
    
    // Load CSS files sequentially (Bootstrap first, then theme CSS)
    for (let i = 0; i < themeAssets.styles.length; i++) {
      const cssPath = themeAssets.styles[i]
      const cssUrl = `${ASSET_CONFIG.CDN_BASE_URL}/${themeAssets.folder}${cssPath}`
      const cssId = i === 0 ? 'bootstrap-css' : 'theme-css'
      await loadCSS(cssUrl, cssId)
    }

    // Let the theme-independent jQuery/plugin bundle finish first so legacy
    // theme scripts don't initialize against half-loaded dependencies.
    await waitForCommonAssets()
    
    // Load theme-specific scripts
    for (const scriptPath of themeAssets.scripts) {
      const scriptUrl = `${ASSET_CONFIG.CDN_BASE_URL}/${themeAssets.folder}${scriptPath}`
      await loadScript(scriptUrl, 'theme-script')
    }

    applyThemeBackgroundAssets(config.themeId)
    
    // Apply dynamic color scheme
    applyColorScheme(config.theme.colorScheme, config.themeId)
    
    // Load custom CSS if provided
    if (config.theme.customCSS) {
      await loadCustomCSS(config.theme.customCSS)
    }
    
    // Update favicon: ใช้จาก tenant content (branding.faviconUrl) ถ้ามี ไม่ใช่แค่ theme path
    const faviconUrl = (config.theme.branding?.faviconUrl && config.theme.branding.faviconUrl.trim() !== '')
      ? config.theme.branding.faviconUrl
      : `${ASSET_CONFIG.CDN_BASE_URL}/${themeAssets.folder}/img/favicon.ico`
    updateFavicon(faviconUrl)

    // Update page title
    document.title = config.theme.branding.siteName

    updateSocialMetaTags({
      title: config.theme.branding.siteName,
      description: config.settings?.welcomeMessage || config.settings?.marqueeText || config.theme.branding.siteName,
      imageUrl: config.theme.branding?.logoUrl,
      url: window.location.origin,
    })
  }

  const loadCSS = (url: string, id: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Remove existing CSS with same ID
      const existingLink = document.querySelector(`link[data-css-id="${id}"]`)
      if (existingLink) {
        existingLink.remove()
      }

      // Load new CSS
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = url
      link.setAttribute('data-css-id', id)
      
      link.onload = () => resolve()
      link.onerror = () => reject(new Error(`Failed to load CSS: ${url}`))
      
      document.head.appendChild(link)
    })
  }

  const loadScript = (url: string, id: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Remove existing script with same ID
      const existingScript = document.querySelector(`script[data-script-id="${id}"]`)
      if (existingScript) {
        existingScript.remove()
      }

      // Load new script
      const script = document.createElement('script')
      script.src = url
      script.setAttribute('data-script-id', id)
      
      script.onload = () => {
        // Add jQuery plugins availability check after theme script loads
        if (id === 'theme-script') {
          setTimeout(() => {
            addJQueryPluginsSafety()
          }, 100)
        }
        resolve()
      }
      script.onerror = () => reject(new Error(`Failed to load script: ${url}`))
      
      document.body.appendChild(script)
    })
  }

  // Add safety checks for jQuery plugins to prevent errors
  const addJQueryPluginsSafety = () => {
    try {
      // Check if jQuery is available
      if (typeof window !== 'undefined' && (window as any).$ && (window as any).jQuery) {
        const $ = (window as any).$;
        
        // Add datepicker fallback if not available
        if (!$.fn.datepicker) {
          $.fn.datepicker = function() {
            console.warn('datepicker plugin not loaded, skipping initialization');
            return this;
          };
        }
        
        // Add waypoint fallback if not available
        if (!$.fn.waypoint) {
          $.fn.waypoint = function() {
            console.warn('waypoint plugin not loaded, skipping initialization');
            return this;
          };
        }
        
        // Add other common jQuery plugins fallbacks
        if (!$.fn.owlCarousel) {
          $.fn.owlCarousel = function() {
            console.warn('owlCarousel plugin not loaded, skipping initialization');
            return this;
          };
        }
        
        if (!$.fn.slick) {
          $.fn.slick = function() {
            console.warn('slick plugin not loaded, skipping initialization');
            return this;
          };
        }
        
        console.log('✅ jQuery plugins safety checks added');
      }
    } catch (error) {
      console.warn('Failed to add jQuery plugins safety:', error);
    }
  }

  const loadCustomCSS = (cssContent: string): Promise<void> => {
    return new Promise((resolve) => {
      // Remove existing custom CSS
      const existingStyle = document.querySelector('style[data-custom-css]')
      if (existingStyle) {
        existingStyle.remove()
      }

      // Add custom CSS
      const style = document.createElement('style')
      style.setAttribute('data-custom-css', 'true')
      style.textContent = cssContent
      document.head.appendChild(style)
      
      resolve()
    })
  }

  const CLASSIC_THEME_STYLE_OVERRIDES: Record<string, Record<string, string>> = {
    lenhuay: {
      '--classic-color-yellow': '#ffc833',
      '--classic-color-alert': '#9c1515',
      '--classic-bgc-1': '#ffc833',
      '--classic-bgc-2': '#9c1515',
      '--classic-bgc-5': '#ff5a41',
      '--classic-bgc-15': '#9c1515',
      '--classic-bgg-1': 'linear-gradient(-45deg, #9c1515 25%, #590301 50%, #9c1515 75%, #590301 100%)',
      '--classic-wrap-login-bg': '#9c1515',
      '--classic-bg-fixed': 'linear-gradient(0deg, #dbb94c, #ffc756)',
    },
  }

  const waitForCommonAssets = async (timeoutMs = 5000): Promise<void> => {
    if (typeof window === 'undefined' || (window as any).commonAssetsLoaded) {
      return
    }

    await new Promise<void>((resolve) => {
      const startedAt = Date.now()
      const intervalId = window.setInterval(() => {
        if ((window as any).commonAssetsLoaded || Date.now() - startedAt >= timeoutMs) {
          window.clearInterval(intervalId)
          resolve()
        }
      }, 50)
    })
  }

  const applyColorScheme = (colorScheme: ColorScheme, themeId: string) => {
    const themeMetadata = getThemeMetadata(themeId)
    const classicThemeVariables: Record<string, string> = {
      '--classic-color-yellow': '#e8bf58',
      '--classic-color-alert': '#00a16f',
      '--classic-bgc-1': '#fefca6',
      '--classic-bgc-2': '#50c878',
      '--classic-bgc-4': '#fff',
      '--classic-bgc-5': '#ff5a41',
      '--classic-bgc-15': '#00a16f',
      '--classic-bgg-1': 'linear-gradient(60deg, #00a16f 25%, #004c3d 50%, #00a16f 75%, #004c3d 100%)',
      '--classic-wrap-login-bg': '#00a16f',
      '--classic-bg-fixed': 'linear-gradient(180deg, #efd06d, #804f19)',
      ...(CLASSIC_THEME_STYLE_OVERRIDES[themeMetadata.id] || {}),
    }
    const cssVariables = {
      '--color-primary': colorScheme.primary,
      '--color-secondary': colorScheme.secondary,
      '--color-accent': colorScheme.accent,
      '--color-background': colorScheme.background,
      '--color-text': colorScheme.text,
      '--color-border': colorScheme.border,
      '--color-success': colorScheme.success,
      '--color-warning': colorScheme.warning,
      '--color-danger': colorScheme.danger,
      '--color-info': colorScheme.info,
      ...classicThemeVariables,
    }
    const classicThemeOverrideRules = `
      .bgi-1 {
        background-image: var(--classic-bg-cover-url) !important;
        background-position: center !important;
        background-repeat: no-repeat !important;
        background-size: cover !important;
      }
      .bgi-2 {
        background-image: var(--classic-bg-main-url) !important;
        background-position: center !important;
        background-repeat: no-repeat !important;
        background-size: cover !important;
      }
      .bgi-3 {
        background-image: var(--classic-bg-header-url) !important;
        background-position: center !important;
        background-repeat: no-repeat !important;
        background-size: cover !important;
      }
      .c-yellow { color: var(--classic-color-yellow) !important; }
      .c-red { color: var(--classic-color-alert) !important; }
      .bgc-1 { background-color: var(--classic-bgc-1) !important; }
      .bgc-2 { background-color: var(--classic-bgc-2) !important; }
      .bgc-4 { background-color: var(--classic-bgc-4) !important; }
      .bgc-5 { background-color: var(--classic-bgc-5) !important; }
      .bgc-15 { background-color: var(--classic-bgc-15) !important; }
      .bgg-1 { background: var(--classic-bgg-1) !important; }
      .wrap-login { background: var(--classic-wrap-login-bg) !important; }
      .bg-fixed { background: var(--classic-bg-fixed) !important; }
    `

    // Remove existing color scheme
    const existingStyle = document.querySelector('style[data-color-scheme]')
    if (existingStyle) {
      existingStyle.remove()
    }

    // Apply new color scheme as CSS custom properties
    const style = document.createElement('style')
    style.setAttribute('data-color-scheme', 'true')
    style.textContent = `
      :root {
${Object.entries(cssVariables)
  .map(([key, value]) => `        ${key}: ${value};`)
  .join('\n')}
      }
${classicThemeOverrideRules}
    `
    document.head.appendChild(style)
  }

  const updateFavicon = (url: string) => {
    const faviconUrl = url && url.trim() !== '' ? url : '/favicon.svg'
    const iconDefinitions = [
      { rel: 'icon', type: faviconUrl.endsWith('.svg') ? 'image/svg+xml' : 'image/x-icon' },
      { rel: 'shortcut icon', type: faviconUrl.endsWith('.svg') ? 'image/svg+xml' : 'image/x-icon' },
    ]

    iconDefinitions.forEach(({ rel, type }) => {
      let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null
      if (!link) {
        link = document.createElement('link')
        link.rel = rel
        document.head.appendChild(link)
      }
      link.type = type
      link.href = faviconUrl
    })
  }

  // Helper functions
  const mapApiColorsToColorScheme = (apiColors: any, customColors?: any): ColorScheme => {
    // Try to find matching pre-defined color scheme
    const schemeKey = Object.keys(COLOR_SCHEMES).find(key => 
      COLOR_SCHEMES[key].primary === apiColors.primary
    )
    
    if (schemeKey) {
      const baseScheme = COLOR_SCHEMES[schemeKey]
      // Override with custom colors if provided
      return { ...baseScheme, ...customColors }
    }
    
    // Create custom color scheme
    return {
      primary: apiColors.primary || '#007bff',
      secondary: apiColors.secondary || '#6c757d', 
      accent: apiColors.accent || '#28a745',
      background: apiColors.background || '#f8f9fa',
      text: apiColors.text || '#212529',
      border: apiColors.border || '#dee2e6',
      success: apiColors.success || '#28a745',
      warning: apiColors.warning || '#ffc107',
      danger: apiColors.danger || '#dc3545',
      info: apiColors.info || '#17a2b8',
      ...customColors
    }
  }

  const getDefaultLogo = (themeId: string): string => {
    const ASSET_CONFIG = getAssetConfig()
    const themeMetadata = getThemeMetadata(themeId)
    return getThemeAssetUrl(ASSET_CONFIG.CDN_BASE_URL, themeMetadata.id, themeMetadata.assets.logo)
  }

  const applyThemeBackgroundAssets = (themeId: string): void => {
    if (typeof document === 'undefined') {
      return
    }

    const ASSET_CONFIG = getAssetConfig()
    const themeMetadata = getThemeMetadata(themeId)
    const root = document.documentElement
    root.dataset.classicThemeId = themeMetadata.id
    root.dataset.classicThemeFolder = themeMetadata.folder
    ;(window as any).__CLASSIC_THEME_FOLDER__ = themeMetadata.folder
    root.style.setProperty(
      '--classic-bg-cover-url',
      `url("${getThemeAssetUrl(ASSET_CONFIG.CDN_BASE_URL, themeId, '/img/bg-cover.jpg')}")`
    )
    root.style.setProperty(
      '--classic-bg-main-url',
      `url("${getThemeAssetUrl(ASSET_CONFIG.CDN_BASE_URL, themeId, '/img/bg.jpg')}")`
    )
    root.style.setProperty(
      '--classic-bg-header-url',
      `url("${getThemeAssetUrl(ASSET_CONFIG.CDN_BASE_URL, themeId, '/img/bg-h.jpg')}")`
    )
  }

  const sanitizeBrandingAssetUrl = (url?: string | null): string => {
    const raw = String(url || '').trim()
    if (!raw) return ''

    try {
      const base = typeof window !== 'undefined' ? window.location.origin : 'https://example.com'
      const currentProtocol = typeof window !== 'undefined' ? window.location.protocol : 'https:'
      const currentHost = typeof window !== 'undefined' ? window.location.hostname : ''
      const resolved = new URL(raw, base)
      const isLocalDevHost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(resolved.hostname)
      const cdnBaseUrl = getCdnBaseUrl()
      const normalizedPath = resolved.pathname.startsWith('/') ? resolved.pathname : `/${resolved.pathname}`
      const cdnFallbackUrl = normalizedPath ? `${cdnBaseUrl}${normalizedPath}${resolved.search}${resolved.hash}` : ''

      if (isLocalDevHost && resolved.hostname !== currentHost) {
        return cdnFallbackUrl || ''
      }
      if (currentProtocol === 'https:' && resolved.protocol === 'http:') {
        return cdnFallbackUrl || ''
      }

      return resolved.href
    } catch {
      return raw.startsWith('/') ? raw : ''
    }
  }

  // Function to switch theme dynamically - ✨ รองรับ theme ใดก็ได้จาก configuration
  const switchTheme = async (newThemeId: string) => {
    if (!themeConfig) return
    
    const newThemeMetadata = getThemeById(newThemeId)
    if (!newThemeMetadata) {
      console.error(`Theme ${newThemeId} not found in configuration`)
      setError(`ไม่พบธีม ${newThemeId}`)
      return
    }
    
    setIsLoading(true)
    try {
      if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Switching to theme:', newThemeMetadata.name)
      }
      
      const newConfig: TenantThemeConfig = {
        ...themeConfig,
        themeId: newThemeId,
        theme: {
          ...themeConfig.theme,
          colorScheme: mapApiColorsToColorScheme(newThemeMetadata.colorScheme),
          branding: {
            ...themeConfig.theme.branding,
            siteName: `${newThemeMetadata.name} :: แทงหวยออนไลน์`,
            logoUrl: getDefaultLogo(newThemeId)
          }
        }
      }
      
      await loadThemeAssets(newConfig)
      setThemeConfig(newConfig)
      
      // Store preference in localStorage
      localStorage.setItem('huay-classic-theme', newThemeId)
      
      if (process.env.NODE_ENV === 'development') {
      console.log('✅ Theme switched successfully to:', newThemeMetadata.name)
      }
      
    } catch (error) {
      console.error('Failed to switch theme:', error)
      setError(`ไม่สามารถเปลี่ยนธีม ${newThemeMetadata.name} ได้`)
    } finally {
      setIsLoading(false)
    }
  }



  const createDefaultThemeConfigSafe = (tenantId: string): TenantThemeConfig => {
    return {
      tenantId,
      themeId: 'huay-asia',
      theme: {
        baseTheme: 'classic',
        colorScheme: COLOR_SCHEMES['asia-green'],
        branding: {
          siteName: 'HuayAsia :: แทงหวยออนไลน์',
          logoUrl: getDefaultLogo('huay-asia'),
          faviconUrl: getThemeAssetUrl(getAssetConfig().CDN_BASE_URL, 'huay-asia', '/img/favicon.ico')
        }
      },
      settings: {
        marqueeText: 'ยินดีต้อนรับทุกท่านเข้าสู่เว็บ lenhuay.net เว็บหวยออนไลน์ที่มาแรงที่สุดในตอนนี้ มั่นคง ซื่อตรง รับประกันจ่ายจริงทุกโพย',
        sessionIdleMinutes: 30
      }
    }
  }

  // ── System Maintenance — highest priority, shown to ALL visitors ─────────
  if (maintenance.enabled) {
    return (
      <SystemMaintenancePage
        reason={maintenance.reason}
        estimatedEnd={maintenance.estimatedEnd}
        services={maintenance.services}
        logoUrl={themeConfig?.theme?.branding?.logoUrl}
        siteName={themeConfig?.theme?.branding?.siteName}
      />
    )
  }
  // ── Maintenance Warning overlay (countdown before maintenance kicks in) ───

  if (viewAsHandling) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-dark">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-white">กำลังเข้า View As...</p>
        </div>
      </div>
    )
  }

  if (viewAsError) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-dark">
        <div className="text-center text-white">
          <h4>View As ไม่สำเร็จ</h4>
          <p className="text-warning">{viewAsError}</p>
          <button className="btn btn-light mt-2" onClick={() => window.location.reload()}>
            ลองใหม่
          </button>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-dark">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-white">กำลังโหลดธีม...</p>
        </div>
      </div>
    )
  }

  if (error || !themeConfig) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-danger">
        <div className="text-center text-white">
          <h4>เกิดข้อผิดพลาด</h4>
          <p>{error || 'ไม่สามารถโหลดธีมได้'}</p>
          <button 
            className="btn btn-light"
            onClick={() => window.location.reload()}
          >
            ลองใหม่
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Maintenance warning countdown overlay (shown before actual maintenance) */}
      {maintWarn.active && <MaintenanceWarningOverlay warning={maintWarn} />}

      <Router>
        <AuthProvider>
          <BankProvider tenantId={tenantId}>
            <WalletProvider>
              <UserInfoProvider>
                <TenantThemeContext.Provider value={{ themeConfig, isLoading, error }}>
                  <TenantStatusGuard tenantId={tenantId}>
                    <AppRoutes 
                      themeConfig={themeConfig} 
                      assetConfig={getAssetConfig()}
                    />
                  </TenantStatusGuard>
                </TenantThemeContext.Provider>
              </UserInfoProvider>
            </WalletProvider>
          </BankProvider>
        </AuthProvider>
      </Router>
      
      {/* Development Tools Panel - Now includes Auth */}
      <AuthProvider>
        <DevToolsPanel
          themeConfig={themeConfig}
          switchTheme={switchTheme}
          isConnected={isConnected}
          authType={authType}
          webSocketEnabled={webSocketEnabled}
          tenantId={tenantId}
          popupManagerEnabled={popupManagerEnabled}
          currentPopup={currentPopup}
          popupQueue={popupQueue}
          popupLoading={popupLoading}
          popupError={popupError}
          disconnectWebSocket={disconnectWebSocket}
          refreshPopups={refreshPopups}
          forceShowPopup={forceShowPopup}
        />
      </AuthProvider>

      {/* Popup Modal - Using Portal to render outside React tree */}
      {currentPopup && tenantId && themeConfig && (
        <PopupModal
          key={currentPopup._id}
          popup={currentPopup}
          tenantId={tenantId}
          onClose={dismissPopup}
        />
      )}

      {/* ประกาศจากแอดมิน — แสดง Sweet Alert เมื่อมีประกาศ (รองรับส่งทันทีผ่าน WebSocket) */}
      {tenantId ? <ClassicAnnouncementAlert tenantId={tenantId} key="announcement" /> : null}

      {/* Rate Limiting Toast Notifications */}
      <Toaster
        position="top-right"
        gutter={12}
        containerClassName="toast-container"
        containerStyle={{
          top: '24px',
          right: '24px',
          zIndex: 999999,
          overflow: 'visible',
          maxWidth: '420px',
          minWidth: '300px'
        }}
        toastOptions={{
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #1f2937, #111827)',
            color: '#ffffff',
            fontSize: '15px',
            fontWeight: '500',
            borderRadius: '16px',
            padding: '16px 20px',
            maxWidth: '400px',
            minWidth: '280px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04), 0 0 0 1px rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(16px)',
            zIndex: 999999,
            position: 'relative'
          },
          success: {
            duration: 4000,
            style: {
              background: 'linear-gradient(135deg, #10b981, #059669)',
              color: '#ffffff',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              boxShadow: '0 20px 25px -5px rgba(16, 185, 129, 0.2), 0 10px 10px -5px rgba(16, 185, 129, 0.1)'
            },
            iconTheme: {
              primary: '#ffffff',
              secondary: '#10b981'
            }
          },
          error: {
            duration: 6000,
            style: {
              background: 'linear-gradient(135deg, #ef4444, #dc2626)',
              color: '#ffffff',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              boxShadow: '0 20px 25px -5px rgba(239, 68, 68, 0.2), 0 10px 10px -5px rgba(239, 68, 68, 0.1)'
            },
            iconTheme: {
              primary: '#ffffff',
              secondary: '#ef4444'
            }
          },
          loading: {
            style: {
              background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
              color: '#ffffff',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              boxShadow: '0 20px 25px -5px rgba(59, 130, 246, 0.2), 0 10px 10px -5px rgba(59, 130, 246, 0.1)'
            }
          }
        }}
      />
      

      
      {/* Beautiful Toast Styling */}
      <style dangerouslySetInnerHTML={{
        __html: `
          /* Force toast container to top-right with proper spacing */
          div[data-hot-toast-container] {
            position: fixed !important;
            top: 24px !important;
            right: 24px !important;
            left: auto !important;
            bottom: auto !important;
            z-index: 999999 !important;
            pointer-events: none !important;
            max-width: 420px !important;
            min-width: 300px !important;
            transform: none !important;
            overflow: visible !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          
          /* Ensure parent containers don't clip toast */
          body, html, #root, [data-reactroot] {
            overflow-x: visible !important;
          }
          
          /* Ensure container doesn't get clipped by viewport */
          @media (max-width: 768px) {
            div[data-hot-toast-container] {
              right: 12px !important;
              max-width: calc(100vw - 24px) !important;
            }
          }
          
          /* Beautiful toast animations */
          div[data-hot-toast-container] > div {
            pointer-events: auto !important;
            z-index: 999999 !important;
            position: relative !important;
            margin-bottom: 16px !important;
            transform: translateX(0) !important;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
            overflow: visible !important;
            max-width: 100% !important;
            width: 100% !important;
          }
          
          /* Individual toast styling */
          div[data-hot-toast] {
            max-width: 420px !important;
            min-width: 300px !important;
            width: auto !important;
            overflow: visible !important;
            word-wrap: break-word !important;
            white-space: normal !important;
          }
          
          /* Hover effects for toasts */
          div[data-hot-toast-container] > div:hover {
            transform: translateY(-2px) scale(1.02) !important;
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
          }
          
          /* Override any conflicting positioning */
          .toast-container {
            position: fixed !important;
            top: 24px !important;
            right: 24px !important;
            left: auto !important;
            bottom: auto !important;
            z-index: 999999 !important;
            overflow: visible !important;
          }
          
          /* Beautiful entrance animation */
          div[data-hot-toast-container] > div[data-hot-toast] {
            animation: slideInFromRight 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
          }
          
          @keyframes slideInFromRight {
            from {
              transform: translateX(100%) scale(0.9);
              opacity: 0;
            }
            to {
              transform: translateX(0) scale(1);
              opacity: 1;
            }
          }
          
          /* Force override any CSS frameworks */
          div[data-hot-toast-container][style] {
            top: 24px !important;
            right: 24px !important;
            left: auto !important;
            bottom: auto !important;
            position: fixed !important;
            overflow: visible !important;
          }
          
          /* Glassmorphism effect enhancement */
          div[data-hot-toast-container] > div {
            backdrop-filter: blur(20px) saturate(180%) !important;
            -webkit-backdrop-filter: blur(20px) saturate(180%) !important;
            border: 1px solid rgba(255, 255, 255, 0.1) !important;
          }
          
          /* Ensure toast content is fully visible */
          div[data-hot-toast] > div {
            overflow: visible !important;
            max-width: 100% !important;
          }
          
          /* Prevent any clipping from parent elements */
          div[data-hot-toast-container],
          div[data-hot-toast-container] > div,
          div[data-hot-toast] {
            clip: auto !important;
            clip-path: none !important;
          }

          /* Kicked user modal styling - Center text and red color */
          .kicked-popup {
            text-align: center !important;
          }
          
          .kicked-popup .kicked-title {
            color: #dc2626 !important;
            font-weight: bold !important;
            text-align: center !important;
            font-size: 24px !important;
          }
          
          .kicked-popup .kicked-content {
            text-align: center !important;
            color: #dc2626 !important;
          }
          
          .kicked-popup .kicked-content > div {
            text-align: center !important;
          }
          
          .kicked-popup .kicked-content strong {
            color: #dc2626 !important;
            font-weight: bold !important;
          }
          
          .kicked-popup .swal2-icon {
            border-color: #dc2626 !important;
            color: #dc2626 !important;
          }
          
          .kicked-popup .swal2-timer-progress-bar {
            background-color: #dc2626 !important;
          }
        `
      }} />
    </>
  )
}

export default App
